import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, ProgressBar, Alert, Badge } from 'react-bootstrap';
import { 
  CheckCircle, 
  Circle, 
  Cloud, 
  Globe, 
  Shield, 
  Envelope, 
  HourglassSplit, 
  Megaphone,
  Plus,
  ArrowRight,
  InfoCircle
} from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import { useTestAWSConnectionQuery, useGetDomainsQuery, useGetEmailAccountsQuery, useCheckEmailReceivingSetupQuery, useCheckBounceComplaintSetupQuery } from '../services/api';
import { useAccount } from '../contexts/AccountContext';
import { useUser } from '../contexts/UserContext';
import EmailReceivingSetupStatus from './EmailReceivingSetupStatus';
import BounceComplaintSetupStatus from './BounceComplaintSetupStatus';

const Onboarding = ({ onForceHide }) => {
  const navigate = useNavigate();
  const { selectedAccount, accounts } = useAccount();
  
  // Check completion status for each step
  const { data: awsConnection } = useTestAWSConnectionQuery();
  const { data: domainsData } = useGetDomainsQuery();
  const { data: emailAccountsData } = useGetEmailAccountsQuery();
  // Get current user's email for bounce/complaint setup check
  const { currentUser } = useUser();
  const userEmail = currentUser?.email || '';
  
  const { data: bounceComplaintSetupData } = useCheckBounceComplaintSetupQuery(userEmail || null, {
    skip: !userEmail, // Skip query if no user email
    refetchOnMountOrArgChange: true
  });

  const [completedSteps, setCompletedSteps] = useState(new Set());

  // Define onboarding steps
  const steps = [
    {
      id: 1,
      title: 'Connect to Your AWS Account',
      description: 'Connect your AWS account to enable email marketing functionality',
      icon: Cloud,
      route: '/aws-accounts',
      checkCompletion: () => {
        // Check if AWS connection is successful
        return awsConnection?.data?.connected === true;
      }
    },
    {
      id: 2,
      title: 'Domain Name Setup',
      description: 'Add or connect your domain name(s). Buy a domain or connect existing domains via TXT records',
      icon: Globe,
      route: '/domains',
      checkCompletion: (filteredDomains) => {
        // Check if at least one domain exists (account-specific filtered domains passed in)
        return (filteredDomains || []).length > 0;
      }
    },
    {
      id: 3,
      title: 'Verify Your Domain',
      description: 'Complete SPF, DKIM, and DMARC verification for your domain',
      icon: Shield,
      route: '/domains',
      checkCompletion: () => {
        // Check if at least one domain is fully verified (SPF, DKIM, DMARC)
        const domains = domainsData?.data?.domains || [];
        return domains.some(domain => 
          domain.spfStatus === 'configured' &&
          domain.dkimStatus === 'configured' &&
          domain.dmarcStatus === 'configured' &&
          domain.verificationStatus === 'verified'
        );
      }
    },
    {
      id: 4,
      title: 'SES Identity Setup',
      description: 'Set up SES for your domain and ensure you\'re approved for production use (not sandbox)',
      icon: Envelope,
      route: '/email-accounts',
      checkCompletion: () => {
        // Check if SES is set up (we can check if email accounts can be created)
        // For now, if domains are verified, assume SES setup is possible
        const domains = domainsData?.data?.domains || [];
        return domains.some(domain => 
          domain.verificationStatus === 'verified' &&
          domain.spfStatus === 'configured' &&
          domain.dkimStatus === 'configured'
        );
      }
    },
    {
      id: 5,
      title: 'Create Email Accounts',
      description: 'Create email accounts for sending marketing emails',
      icon: Envelope,
      route: '/email-accounts',
      checkCompletion: () => {
        // Check if at least one email account exists
        const emailAccounts = emailAccountsData?.data?.emailAccounts || [];
        return emailAccounts.length > 0;
      }
    },
    {
      id: 5.5,
      title: 'Setup Email Receiving (Unibox)',
      description: 'Configure email receiving so you can receive emails in your Unibox inbox',
      icon: Envelope,
      route: '/email-accounts',
      checkCompletion: () => {
        // Check if email receiving is fully configured
        const emailAccounts = emailAccountsData?.data?.emailAccounts || [];
        if (emailAccounts.length === 0) return false;
        
        // Get verified email addresses
        const verifiedEmails = emailAccounts
          .filter(acc => acc.status === 'verified' || acc.status === 'active')
          .map(acc => acc.email || `${acc.username}@${acc.domain}`);
        
        // This will be checked via API - for now return true if we have verified accounts
        return verifiedEmails.length > 0;
      }
    },
    {
      id: 5.6,
      title: 'Setup Bounce/Complaint Handling',
      description: 'Configure bounce and complaint handling for SES production access (required)',
      icon: Shield,
      route: '/email-accounts',
      checkCompletion: () => {
        // Check if bounce/complaint handling is fully configured
        const setupData = bounceComplaintSetupData?.data;
        return setupData?.allPassed === true;
      }
    },
    {
      id: 6,
      title: 'Warm Up Email Accounts',
      description: 'Begin the email warming process for your new email accounts',
      icon: HourglassSplit,
      route: '/email-accounts',
      checkCompletion: () => {
        // Check if at least one email account is warming up or warmed
        const emailAccounts = emailAccountsData?.data?.emailAccounts || [];
        return emailAccounts.some(account => 
          account.status === 'warming' || 
          account.status === 'active' ||
          account.warmupStatus === 'in_progress' ||
          account.warmupStatus === 'completed'
        );
      }
    },
    {
      id: 7,
      title: 'Create Your First Campaign',
      description: 'While your emails are warming up, create your first marketing campaign',
      icon: Megaphone,
      route: '/campaigns',
      checkCompletion: () => {
        // TODO: Check if at least one campaign exists (when campaigns API is ready)
        return false; // Placeholder - will implement when campaigns are ready
      }
    }
  ];

  // Check completion status on mount and when data changes - ACCOUNT SPECIFIC
  useEffect(() => {
    const newCompletedSteps = new Set();
    
    // Filter data by selected account if not "all"
    let filteredDomains = domainsData?.data?.domains || [];
    let filteredEmailAccounts = emailAccountsData?.data?.emailAccounts || [];
    
    // CRITICAL: Filter by selected account to ensure account-specific data
    if (selectedAccount && selectedAccount.id && selectedAccount.id !== 'all') {
      // Get the account's domains from AccountContext
      const accountDomains = selectedAccount.domains || [];
      
      // Filter domains by account's known domains
      // Match domains based on domain name (API may not have accountId field yet)
      filteredDomains = filteredDomains.filter(domain => 
        accountDomains.some(accDomain => 
          domain.domain === accDomain || 
          domain.domain?.endsWith(accDomain) ||
          accDomain.includes(domain.domain)
        )
      );
      
      // For email accounts, we need to filter by domain
      // Email accounts belong to domains, and domains belong to accounts
      const accountDomainNames = accountDomains.map(d => d.toLowerCase());
      filteredEmailAccounts = filteredEmailAccounts.filter(emailAccount => {
        const emailDomain = emailAccount.domain?.toLowerCase();
        return accountDomainNames.some(accDomain => 
          emailDomain === accDomain ||
          emailDomain?.endsWith(`.${accDomain}`) ||
          emailDomain?.includes(accDomain)
        );
      });
    }
    
    // Create account-specific closure functions for step checking
    const accountFilteredDomains = filteredDomains;
    const accountFilteredEmailAccounts = filteredEmailAccounts;
    
    steps.forEach(step => {
      // Re-evaluate completion with account-specific filtered data
      let isComplete = false;
      
      if (step.id === 1) {
        // Step 1: Connect AWS - check if AWS connection is successful FOR THIS ACCOUNT
        // AWS connection test might be account-specific via API, but for now check if connected
        isComplete = awsConnection?.data?.connected === true;
      } else if (step.id === 2) {
        // Step 2: Domain Name Setup
        isComplete = accountFilteredDomains.length > 0;
      } else if (step.id === 3) {
        // Step 3: Verify Domain
        isComplete = accountFilteredDomains.some(domain => 
          domain.spfStatus === 'configured' &&
          domain.dkimStatus === 'configured' &&
          domain.dmarcStatus === 'configured' &&
          domain.verificationStatus === 'verified'
        );
      } else if (step.id === 4) {
        // Step 4: SES Identity Setup
        isComplete = accountFilteredDomains.some(domain => 
          domain.verificationStatus === 'verified' &&
          domain.spfStatus === 'configured' &&
          domain.dkimStatus === 'configured'
        );
      } else if (step.id === 5) {
        // Step 5: Create Email Accounts
        isComplete = accountFilteredEmailAccounts.length > 0;
      } else if (step.id === 5.5) {
        // Step 5.5: Setup Email Receiving (Unibox)
        // Check if email receiving is fully configured
        if (accountFilteredEmailAccounts.length === 0) {
          isComplete = false;
        } else {
          // Get verified email addresses
          const verifiedEmails = accountFilteredEmailAccounts
            .filter(acc => acc.status === 'verified' || acc.status === 'active' || acc.verificationStatus === 'Success')
            .map(acc => acc.fullEmail || `${acc.email}@${acc.domain}`);
          // For now, if we have verified accounts, assume setup is done
          // The EmailReceivingSetupStatus component will do a thorough check
          isComplete = verifiedEmails.length > 0;
        }
      } else if (step.id === 5.6) {
        // Step 5.6: Setup Bounce/Complaint Handling
        // Check if bounce/complaint handling is fully configured
        const setupData = bounceComplaintSetupData?.data;
        isComplete = setupData?.allPassed === true;
      } else if (step.id === 6) {
        // Step 6: Warm Up Email Accounts
        isComplete = accountFilteredEmailAccounts.some(account => 
          account.status === 'warming' || 
          account.status === 'active' ||
          account.warmupStatus === 'in_progress' ||
          account.warmupStatus === 'completed'
        );
      } else if (step.id === 7) {
        // Step 7: Create First Campaign - TODO: implement when campaigns API is ready
        // For now, this will always be false (not complete)
        isComplete = false; // TODO: Check if campaigns exist for this account
      }
      
      if (isComplete) {
        newCompletedSteps.add(step.id);
      }
    });
    
    setCompletedSteps(newCompletedSteps);
  }, [awsConnection, domainsData, emailAccountsData, bounceComplaintSetupData, selectedAccount]);

  // Calculate progress
  const completedCount = completedSteps.size;
  const totalSteps = steps.length;
  const progress = (completedCount / totalSteps) * 100;

  // Get step icon and color
  const getStepStatus = (stepId) => {
    if (completedSteps.has(stepId)) {
      return { icon: CheckCircle, color: 'success', badge: 'Completed' };
    }
    return { icon: Circle, color: 'secondary', badge: 'Pending' };
  };

  // Check if onboarding is complete
  const isOnboardingComplete = completedSteps.size === totalSteps;

  return (
    <Container fluid className="p-4">
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header className="bg-primary text-white">
              <Row className="align-items-center">
                <Col>
                  <h3 className="mb-0">
                    Welcome! Let's Get You Started
                    {selectedAccount?.alias && selectedAccount.id !== 'all' && (
                      <Badge bg="light" text="dark" className="ms-2">
                        {selectedAccount.alias}
                      </Badge>
                    )}
                  </h3>
                  <p className="mb-0 mt-2">
                    {selectedAccount?.alias && selectedAccount.id !== 'all' 
                      ? `Follow these steps to set up ${selectedAccount.alias} for email marketing`
                      : 'Follow these steps to set up your email marketing platform'}
                  </p>
                </Col>
                <Col xs="auto">
                  <Button
                    variant="light"
                    size="sm"
                    onClick={() => navigate('/aws-accounts')}
                    className="me-2"
                  >
                    <Plus className="me-2" />
                    Add AWS Account
                  </Button>
                  {accounts && accounts.length > 0 && (
                    <>
                      {onForceHide && (
                        <Button
                          variant="outline-light"
                          size="sm"
                          onClick={() => onForceHide()}
                          className="me-2"
                        >
                          Back to Dashboard
                        </Button>
                      )}
                      {!onForceHide && (
                        <Button
                          variant="outline-light"
                          size="sm"
                          onClick={() => navigate('/dashboard')}
                        >
                          View Dashboard
                        </Button>
                      )}
                    </>
                  )}
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              {/* Progress Bar */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="mb-0">Setup Progress</h5>
                  <Badge bg={isOnboardingComplete ? 'success' : 'primary'}>
                    {completedCount} of {totalSteps} steps completed
                  </Badge>
                </div>
                <ProgressBar 
                  now={progress} 
                  variant={isOnboardingComplete ? 'success' : 'primary'}
                  className="mb-3"
                  style={{ height: '25px' }}
                />
                {isOnboardingComplete && (
                  <Alert variant="success" className="mb-0">
                    <CheckCircle className="me-2" />
                    <strong>Congratulations!</strong> You've completed all setup steps. You're ready to start sending marketing emails!
                  </Alert>
                )}
              </div>

              {/* Steps List */}
              <div className="onboarding-steps">
                {steps.map((step, index) => {
                  const status = getStepStatus(step.id);
                  const StatusIcon = status.icon;
                  const isCompleted = completedSteps.has(step.id);
                  // Step 1 (index 0) should always be active if not completed
                  // Other steps are active if previous step is completed
                  const isActive = !isCompleted && (index === 0 || completedSteps.has(steps[index - 1]?.id));

                  return (
                    <Card 
                      key={step.id} 
                      className={`mb-3 ${isCompleted ? 'border-success' : isActive ? 'border-primary' : 'border-secondary'}`}
                      style={{ 
                        opacity: !isActive && !isCompleted ? 0.6 : 1,
                        transition: 'all 0.3s ease',
                        display: 'block' // Ensure card is always visible
                      }}
                    >
                      <Card.Body>
                        <Row className="align-items-center">
                          <Col xs="auto">
                            <StatusIcon 
                              size={32} 
                              className={`text-${status.color}`}
                              style={{ minWidth: '32px' }}
                            />
                          </Col>
                          <Col>
                            <div className="d-flex align-items-center mb-1">
                              <h5 className="mb-0 me-2">
                                Step {step.id}: {step.title}
                              </h5>
                              <Badge bg={status.color}>{status.badge}</Badge>
                            </div>
                            <p className="text-muted mb-2">{step.description}</p>
                            {!isCompleted && (
                              <Button
                                variant={isActive ? 'primary' : 'outline-primary'}
                                size="sm"
                                onClick={() => navigate(step.route)}
                                disabled={!isActive}
                              >
                                {isActive ? (
                                  <>
                                    Get Started <ArrowRight className="ms-1" />
                                  </>
                                ) : (
                                  <>
                                    View Step <ArrowRight className="ms-1" />
                                  </>
                                )}
                              </Button>
                            )}
                            {isCompleted && (
                              <Alert variant="success" className="mb-0 mt-2 py-2">
                                <CheckCircle className="me-2" />
                                <small>This step has been completed!</small>
                              </Alert>
                            )}
                            
                            {/* Show setup status check for email receiving step */}
                            {step.id === 5.5 && (
                              <div className="mt-3">
                                {(() => {
                                  const emailAccounts = emailAccountsData?.data?.emailAccounts || [];
                                  const verifiedEmails = emailAccounts
                                    .filter(acc => acc.status === 'verified' || acc.status === 'active' || acc.verificationStatus === 'Success')
                                    .map(acc => acc.fullEmail || `${acc.email}@${acc.domain}`);
                                  return <EmailReceivingSetupStatus emailAddresses={verifiedEmails} />;
                                })()}
                              </div>
                            )}
                            {/* Show setup status check for bounce/complaint handling step */}
                            {step.id === 5.6 && (
                              <div className="mt-3">
                                <BounceComplaintSetupStatus />
                              </div>
                            )}
                          </Col>
                          <Col xs="auto">
                            {step.icon && <step.icon size={24} className="text-muted" />}
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>

              {/* Info Alert */}
              <Alert variant="info" className="mt-4">
                <InfoCircle className="me-2" />
                <strong>Tip:</strong> You can connect unlimited AWS accounts to this platform. Each account can have multiple domains and email identities. Use the "Add AWS Account" button to manage additional accounts.
              </Alert>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Onboarding;

